import type {
  PredictionCategoryTreeResponse,
  PredictionProductsParams,
  PredictionProductsResponse,
  PredictionContractListParams,
  PredictionContractListResponse,
  PredictionClosingResponse,
  PredictionProductConditionalParams,
  PredictionProductConditionalResponse,
} from '@/types/prediction.types';
import { PREDICTION_API_PATHS } from './api-paths';
import { requester } from '@ibgroup-tws-webapps/ratio.ui';
import { getInternalHeaders } from '@/utils/restricted';
import {
  overrideContractListMeasuredPeriods,
  overrideProductMeasuredPeriods,
} from '@/utils/predictionMeasuredPeriodOverrides';

export const getCategoryTree = async (usePublicApi = false): Promise<PredictionCategoryTreeResponse> => {
  const response = await requester.request(PREDICTION_API_PATHS.CATEGORY_TREE(usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getProducts = async (
  params: PredictionProductsParams,
  usePublicApi = false,
): Promise<PredictionProductsResponse> => {
  if (!params.endpoint) {
    throw new Error('Product endpoint is required');
  }
  const response = await requester.request(PREDICTION_API_PATHS.PRODUCT(params, usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return overrideProductMeasuredPeriods(await response.json());
};

export const getContractList = async (
  params: PredictionContractListParams,
  usePublicApi = false,
): Promise<PredictionContractListResponse> => {
  if (!params.conid) {
    throw new Error('Contract ID is required');
  }
  if (!params.exchange) {
    throw new Error('Exchange is required');
  }
  const response = await requester.request(PREDICTION_API_PATHS.CONTRACT_LIST(params, usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return overrideContractListMeasuredPeriods(await response.json());
};

export const getProductConditional = async (
  params: PredictionProductConditionalParams,
  usePublicApi = false,
): Promise<PredictionProductConditionalResponse> => {
  if (!params.conid) {
    throw new Error('Contract ID is required');
  }
  if (!params.exchange) {
    throw new Error('Exchange is required');
  }
  const response = await requester.request(PREDICTION_API_PATHS.PRODUCT_CONDITIONAL(params, usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getClosingContracts = async (usePublicApi = false): Promise<PredictionClosingResponse> => {
  const response = await requester.request(PREDICTION_API_PATHS.CONTRACT_CLOSING(usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};
// last modified by AI tools on 2026-10-26
